import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import { TrendingUp, TrendingDown, Minus, Trophy, Target, Flame, Zap } from "lucide-react";
import { API_BASE as API_URL } from "@/config/api";
import { useTranslation } from "react-i18next";
import { makeTr } from "@/i18n/tr";

export default function RatingStats({ token }) {
  const { i18n } = useTranslation();
  const tr = makeTr(i18n.language);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, [token]);

  const loadStats = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API_URL}/rating/stats`, { headers });
      setStats(res.data);
    } catch (err) {
      console.error("Failed to load rating stats:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass">
        <CardContent className="py-10 text-center">
          <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto" />
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return null;
  }

  const getStreakIcon = () => {
    if (stats.streak?.type === "win") return <Flame className="h-4 w-4 text-green-500" />;
    if (stats.streak?.type === "loss") return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-yellow-500" />;
  };

  const getStreakText = () => {
    if (!stats.streak || stats.streak.count === 0) return tr("Нет серии", "No streak");
    const count = stats.streak.count;
    if (stats.streak.type === "win") return `${count} ${tr("побед подряд", "wins in a row")}`;
    if (stats.streak.type === "loss") return `${count} ${tr("поражений подряд", "losses in a row")}`;
    return `${count} ${tr("ничьих подряд", "draws in a row")}`;
  };

  return (
    <div className="space-y-4">
      {/* Main Rating Card */}
      <Card className="glass border-primary/30">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            {tr("Elo Рейтинг", "Elo Rating")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-5xl font-black text-primary">{stats.current_rating}</p>
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-green-500" />
                  {tr("Макс", "Max")}: {stats.highest_rating}
                </span>
                <span className="flex items-center gap-1">
                  <TrendingDown className="h-3 w-3 text-red-500" />
                  {tr("Мин", "Min")}: {stats.lowest_rating}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end">
                {getStreakIcon()}
                <span className="text-sm font-medium">{getStreakText()}</span>
              </div>
              <Badge variant="outline" className="mt-2">
                <Zap className="h-3 w-3 mr-1" />
                {stats.games_played} {tr("игр", "games")}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="glass">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-500">{stats.wins}</p>
            <p className="text-xs text-muted-foreground">{tr("Побед", "Wins")}</p>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-500">{stats.losses}</p>
            <p className="text-xs text-muted-foreground">{tr("Поражений", "Losses")}</p>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-500">{stats.draws}</p>
            <p className="text-xs text-muted-foreground">{tr("Ничьих", "Draws")}</p>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-primary">{stats.win_rate}%</p>
            <p className="text-xs text-muted-foreground">{tr("Процент побед", "Winrate")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Rating Chart */}
      {stats.rating_trend && stats.rating_trend.length > 1 && (
        <Card className="glass">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Target className="h-4 w-4" />
              {tr("История рейтинга", "Rating history")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.rating_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#666"
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis 
                    stroke="#666"
                    domain={['auto', 'auto']}
                    tick={{ fontSize: 10 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1a1a1a', 
                      border: '1px solid #333',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="rating" 
                    stroke="#22c55e" 
                    strokeWidth={2}
                    dot={{ fill: '#22c55e', strokeWidth: 0, r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Average Opponent */}
      {stats.average_opponent_rating > 0 && (
        <Card className="glass">
          <CardContent className="p-4 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{tr("Средний рейтинг соперников", "Average opponent rating")}</span>
            <span className="font-bold text-lg">{stats.average_opponent_rating}</span>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
